import { type ReactNode } from "react";
import { ThemeProvider, type Theme } from "../../context/ThemeContext";
import GmailWrapper, { type EmailData } from "../GmailWrapper";
import type { EmailOption } from "../GmailWrapper/EmailContext";

export { useTheme, type Theme } from "../../context/ThemeContext";
export type { EmailData, EmailOption };

interface GmailInboxProps {
  /** Function to fetch recipient suggestions based on input. */
  fetchEmailOptions: (currentEmail: string) => Promise<EmailOption[]>;
  /** Callback triggered when the email is "sent" or updated. */
  handleChange: (emailData: EmailData) => void;
  /** Initial values for the email fields (to, subject, body, etc.). */
  initialData?: Partial<EmailData>;
  /** Default theme for the inbox component ('light' or 'dark'). Defaults to 'dark'. */
  initialTheme?: Theme;
  /** Callback triggered when the theme is toggled. */
  onThemeChange?: (theme: Theme) => void;
  /** Component to render on the left side of the toolbar. */
  leftChildren?: ReactNode;
  /** Component to render on the right side of the toolbar. */
  rightChildren?: ReactNode;
  /** Custom tool to render inside the plugin-wrapper. */
  customTool?: ReactNode;
  /** Placeholder text for the editor. */
  placeholder?: string;
  /** Minimum height of the editor. Defaults to '400px'. */
  minHeight?: string;
  /** If true, only the editor body is rendered, hiding the to/cc/bcc/subject fields. */
  showOnlyBody?: boolean;
  /** If true, the inbox is in reply mode, prefixing the subject with "Re: ". */
  isReply?: boolean;
  /** Optional: Configuration for Google Drive integration. */
  googleDriveConfig?: {
    /** The OAuth 2.0 Client ID for your Google Cloud project. */
    clientId: string;
    /** The API Key for your Google Cloud project. */
    apiKey: string;
    /** Optional: Scopes required for the Drive picker. */
    scopes?: string;
    /** Optional: Discovery doc URLs for Google APIs. */
    discoveryDocs?: string[];
  };
  // AI Integration Props
  /** Custom AI preview component */
  aiContentComponent?: ReactNode;
  /** The AI-generated content */
  aiContent?: string;
  /** Control visibility of AI preview */
  showAiPreview?: boolean;
  /** Callback when AI content is inserted */
  onAiInsert?: (content: string) => void;
  /** Callback when AI content is discarded */
  onAiDiscard?: () => void;
  /** If true, shows the file attachment tool. Defaults to true. */
  showAttachFile?: boolean;
}

function GmailInbox({
  fetchEmailOptions,
  handleChange,
  initialData,
  initialTheme = "dark",
  onThemeChange,
  leftChildren,
  rightChildren,
  customTool,
  showOnlyBody = false,
  isReply = false,
  googleDriveConfig,
  aiContentComponent,
  aiContent,
  showAiPreview,
  onAiInsert,
  onAiDiscard,
  placeholder,
  minHeight,
  showAttachFile = true,
}: GmailInboxProps) {
  return (
    <ThemeProvider initialTheme={initialTheme} onThemeChange={onThemeChange}>
      <GmailWrapper
        fetchEmailOptions={fetchEmailOptions}
        handleChange={handleChange}
        initialData={initialData}
        leftChildren={leftChildren}
        rightChildren={rightChildren}
        customTool={customTool}
        showOnlyBody={showOnlyBody}
        isReply={isReply}
        googleDriveConfig={googleDriveConfig}
        aiContentComponent={aiContentComponent}
        aiContent={aiContent}
        showAiPreview={showAiPreview}
        onAiInsert={onAiInsert}
        onAiDiscard={onAiDiscard}
        placeholder={placeholder}
        minHeight={minHeight}
        showAttachFile={showAttachFile}
      />
    </ThemeProvider>
  );
}

export default GmailInbox;
